<template>
  <footer class="bg-white border-t border-gray-200">
    <div class="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8">
        <!-- Logo + tagline -->
        <div class="flex flex-col gap-3">
          <RouterLink to="/" class="inline-block w-fit">
            <img src="/logos/logo1.png" alt="SupplemEarth" class="h-9 w-auto object-contain" />
          </RouterLink>
          <p class="text-xl text-gray-500 max-w-xs cormorant-text" style="font-style: italic;">
            From the Earth to you
          </p>
        </div>

        <!-- Links + Instagram -->
        <div class="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
          <nav class="flex flex-wrap gap-x-6 gap-y-1 text-sm" aria-label="Footer navigation">
            <RouterLink to="/" class="text-gray-600 hover:text-gray-900 transition-colors">Home</RouterLink>
            <RouterLink to="/products" class="text-gray-600 hover:text-gray-900 transition-colors">Products</RouterLink>
            <RouterLink to="/about" class="text-gray-600 hover:text-gray-900 transition-colors">About Us</RouterLink>
            <RouterLink to="/terms" class="text-gray-600 hover:text-gray-900 transition-colors">Terms</RouterLink>
            <button type="button" @click="openContactModal" class="text-gray-600 hover:text-gray-900 transition-colors text-sm text-left cursor-pointer">Contact</button>
          </nav>
          <div class="flex items-center gap-2">
            <a
              href="https://www.instagram.com/supplemearth.us?igsh=dmJrb2p4dDRjazVi"
              target="_blank"
              rel="noopener noreferrer"
              class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#EDD97A] hover:text-white transition-colors shrink-0"
              aria-label="Instagram"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@supplemearth?_r=1&_t=ZP-94Q95GwqLOx"
              target="_blank"
              rel="noopener noreferrer"
              class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#EDD97A] hover:text-white transition-colors shrink-0"
              aria-label="TikTok"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <!-- Bottom: copyright + payments -->
      <div class="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p class="text-sm text-gray-500">
          © {{ currentYear }} SupplemEarth. All rights reserved.
        </p>
        <div class="flex items-center gap-3">
          <span class="text-xs font-medium text-gray-400 uppercase tracking-wider">We accept</span>
          <div class="flex items-center gap-3">
            <span class="inline-flex items-center justify-center p-1 rounded-md transition-all hover:opacity-90 hover:-translate-y-0.5" title="Visa">
              <img src="/logos/visa.png" alt="Visa" class="h-3 max-h-3 w-auto object-contain block" />
            </span>
            <span class="inline-flex items-center justify-center p-1 rounded-md transition-all hover:opacity-90 hover:-translate-y-0.5" title="Mastercard">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" class="h-5 max-h-5 w-auto block">
                <path fill="#ff9800" d="M32 10A14 14 0 1 0 32 38A14 14 0 1 0 32 10Z"/>
                <path fill="#d50000" d="M16 10A14 14 0 1 0 16 38A14 14 0 1 0 16 10Z"/>
                <path fill="#ff3d00" d="M18,24c0,4.755,2.376,8.95,6,11.48c3.624-2.53,6-6.725,6-11.48s-2.376-8.95-6-11.48 C20.376,15.05,18,19.245,18,24z"/>
              </svg>
            </span>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useModalStore } from '@/stores/modal'

const modalStore = useModalStore()
const currentYear = computed(() => new Date().getFullYear())

function openContactModal() {
  modalStore.openContactModal()
}
</script>
